// Workflow module shell: sub-navigation, global case search, notifications, "New case".
//
// The desktop toolbar is ONE row. It used to be twelve equal pills that wrapped onto three ragged
// lines, with the search box and buttons stranded on the middle one — the dashboard started a third
// of the way down the screen. Now:
//
//   • the six destinations staff move between all day sit in a segmented control (a recessed track
//     with the active view raised out of it), which reads as one object instead of twelve;
//   • the case presets (My work / Urgent / Production) are filtered views of Cases, so they live in
//     a menu under that heading rather than competing with real destinations;
//   • Billing / Reports / Setup — visited occasionally, by fewer people — sit in the same overflow;
//   • search and the actions are pinned right on the SAME row, all 36px tall, so the eye gets one
//     horizontal line rather than a staircase.
//
// On a phone none of that applies: a compact toolbar plus a snapping pill strip.
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { PermissionKey } from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { WfConfigProvider, wfApi } from './workflowApi.ts';
import { NewWfCaseModal } from './WfNewCase.tsx';
import { Btn, inputCls } from './wfUi.tsx';
import { Sheet } from '../../ui/mobile.tsx';
import { Menu, MenuLabel } from '../../ui/Menu.tsx';
import { IconInbox, IconPlus, IconSearch } from '../../ui/icons.tsx';

interface Tab {
  to: string;
  label: string;
  end?: boolean;
  perm?: PermissionKey;
}

/** The views staff move between all day. These stay visible at every width. */
const PRIMARY: Tab[] = [
  { to: '/workflow', label: 'Dashboard', end: true },
  { to: '/workflow/board', label: 'Board' },
  { to: '/workflow/cases', label: 'Cases' },
  { to: '/workflow/compliance', label: 'Compliance' },
  { to: '/workflow/calendar', label: 'Calendar' },
  { to: '/chat', label: 'Chat' },
];

/** Saved filters over the case list — not destinations of their own. */
const PRESETS: Tab[] = [
  { to: '/workflow/cases?preset=my', label: 'My work' },
  { to: '/workflow/cases?preset=urgent', label: 'Urgent' },
  { to: '/workflow/cases?preset=production', label: 'Production queue' },
];

/** Occasional, role-specific screens. */
const SECONDARY: Tab[] = [
  { to: '/workflow/scan', label: 'Scan diary' },
  { to: '/workflow/billing', label: 'Billing', perm: 'finance:read' },
  { to: '/workflow/reports', label: 'Reports', perm: 'workflow:reports' },
  { to: '/workflow/admin', label: 'Setup', perm: 'workflow:configure' },
];

const SEARCH_PLACEHOLDER = 'Search patient, group ID, phone, tracking…';

export function WorkflowLayout() {
  return (
    <WfConfigProvider>
      <Shell />
    </WfConfigProvider>
  );
}

function Shell() {
  const can = useCan();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const searchField = useRef<HTMLInputElement>(null);
  const toolbar = useRef<HTMLDivElement>(null);

  // The toolbar is sticky at the top of the scroll container, and so are some screens' own headers
  // (the case workspace's app bar, for one). Two elements both stuck at top:0 land on top of each
  // other, so the toolbar publishes its measured height and they offset by it. Measured rather than
  // hardcoded because the toolbar's height changes with the viewport and with what it contains.
  useEffect(() => {
    const el = toolbar.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty('--wf-toolbar-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--wf-toolbar-h');
    };
  }, []);

  const refreshUnread = () =>
    wfApi
      .notifications(true)
      .then((r) => setUnread(r.unread))
      .catch(() => undefined);
  useEffect(() => {
    void refreshUnread();
    const t = setInterval(refreshUnread, 60_000);
    return () => clearInterval(t);
  }, []);
  useLiveEvent('workflow.changed', () => void refreshUnread());
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('new') === '1') {
      setShowNew(true);
      navigate('/workflow', { replace: true });
    }
    // Deliberately keyed on the raw query string: this reacts to ?new=1 arriving from the tab
    // bar's "+" action, not to anything in the component's own state.
  }, [window.location.search]);

  // On the two case views the field IS their filter; anywhere else it is "go find a case".
  const FILTERABLE = ['/workflow/cases', '/workflow/board'];
  const onCaseList = FILTERABLE.includes(location.pathname);

  // Entering a screen seeds the box from the URL, so a filtered list shows what it is filtered by.
  useEffect(() => {
    setQ(new URLSearchParams(window.location.search).get('search') ?? '');
  }, [location.pathname]);

  // Live filtering, debounced. Replaces history rather than pushing, or every keystroke would
  // become a back-button step.
  useEffect(() => {
    if (!onCaseList) return;
    const id = window.setTimeout(() => {
      const sp = new URLSearchParams(window.location.search);
      const term = q.trim();
      if ((sp.get('search') ?? '') === term) return;
      if (term) sp.set('search', term);
      else sp.delete('search');
      sp.delete('offset');
      navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [q, onCaseList, navigate, location.pathname]);

  function runSearch(term: string) {
    const v = term.trim();
    if (!v) return;
    setSearchOpen(false);
    // Already on the list: the debounce above has applied it. Elsewhere: go there, including
    // completed and cancelled cases, because a global lookup should find anything.
    if (onCaseList) return;
    setQ('');
    navigate(`/workflow/cases?search=${encodeURIComponent(v)}&includeTerminal=1`);
  }

  const allowed = (t: Tab) => !t.perm || can(t.perm);
  const secondary = SECONDARY.filter(allowed);

  /** A preset is "on" only when its exact query string is showing, not just the path. */
  const presetActive = (to: string) => location.pathname + location.search === to;
  const anyPresetActive = PRESETS.some((p) => presetActive(p.to));

  const menuItem = 'block rounded-md px-3 py-2 text-sm text-ink transition hover:bg-surface-2';

  return (
    <div className="-m-3 flex min-h-full flex-col lg:-m-6">
      <div ref={toolbar} className="sticky top-0 z-sticky border-b border-line bg-surface">
        {/* ---------- Phone: search / inbox / add, then a snapping pill strip ---------- */}
        <div className="px-2 py-2 lg:hidden">
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="tap-row flex min-w-0 flex-1 items-center gap-2 rounded-full border border-line bg-surface-2 px-3.5 py-2.5 text-left text-sm text-muted"
            >
              <IconSearch size={18} />
              <span className="truncate">Search cases</span>
            </button>
            <NavLink
              to="/workflow/inbox"
              aria-label={unread ? `Inbox, ${unread} unread` : 'Inbox'}
              className="tap relative grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink hover:bg-surface-2"
            >
              <IconInbox />
              {unread > 0 && <Badge n={unread} className="right-1 top-1" />}
            </NavLink>
            {can('workflow:create') && (
              <button
                type="button"
                onClick={() => setShowNew(true)}
                aria-label="New case"
                className="tap grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-accent-fg shadow-level-1"
              >
                <IconPlus />
              </button>
            )}
          </div>
          <nav className="no-scrollbar snap-strip -mx-2 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap px-2">
            {[...PRIMARY, ...PRESETS, ...secondary].map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  `tap shrink-0 rounded-full px-3.5 py-2 text-sm font-medium ${
                    t.to.includes('?') ? (presetActive(t.to) ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-ink') : isActive && !anyPresetActive ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-ink'
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* ---------- Desktop: nav row, then the search bar beneath it ---------- */}
        <div className="hidden items-center gap-3 px-4 pb-2 pt-2.5 lg:flex">
          {/* Segmented control: a recessed track, the current view raised out of it. */}
          {/* The strip scrolls rather than wraps if a narrow window can't hold every destination -
              a nav that reflows onto three lines is what this replaced. */}
          <nav
            className="no-scrollbar flex min-w-0 items-center gap-0.5 overflow-x-auto rounded-lg bg-surface-2/80 p-1"
            aria-label="Workflow views"
          >
            {[...PRIMARY, ...secondary].map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  `shrink-0 rounded-md px-3 py-1.5 text-[13px] font-medium tracking-tight transition ${
                    isActive && !anyPresetActive
                      ? 'bg-surface text-ink shadow-level-1'
                      : 'text-muted hover:text-ink'
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}

            {/* Saved filters over the case list. Named "More" rather than shown as a bare ⋯ - an
                unlabelled glyph makes people click it to find out what it is. */}
            {PRESETS.length > 0 && (
              <Menu
                label="More case views"
                triggerClassName={`tap flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] font-medium tracking-tight transition ${
                  anyPresetActive ? 'bg-surface text-ink shadow-level-1' : 'text-muted hover:text-ink'
                }`}
                trigger={
                  <>
                    More
                    <Chevron />
                  </>
                }
              >
                {(close) => (
                  <>
                    <MenuLabel>Case views</MenuLabel>
                    {PRESETS.map((p) => (
                      <NavLink
                        key={p.to}
                        to={p.to}
                        onClick={close}
                        role="menuitem"
                        className={`${menuItem} ${presetActive(p.to) ? 'bg-surface-2 font-semibold' : ''}`}
                      >
                        {p.label}
                      </NavLink>
                    ))}
                  </>
                )}
              </Menu>
            )}
          </nav>

          {/* Actions, pinned right, all the same height as the segmented control. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <NavLink
              to="/workflow/inbox"
              title="Inbox"
              aria-label={unread ? `Inbox, ${unread} unread` : 'Inbox'}
              className={({ isActive }) =>
                `relative grid h-9 w-9 place-items-center rounded-md border transition ${
                  isActive ? 'border-line bg-surface-2 text-ink' : 'border-transparent text-muted hover:bg-surface-2 hover:text-ink'
                }`
              }
            >
              <IconInbox size={18} />
              {unread > 0 && <Badge n={unread} className="-right-1 -top-1" />}
            </NavLink>
            {can('workflow:create') && (
              <Btn tone="accent" onClick={() => setShowNew(true)} className="h-9 gap-1.5 px-3.5">
                <IconPlus size={16} /> New case
              </Btn>
            )}
          </div>
        </div>

        {/* Search gets the full width of its own row. On the case views it filters what is on
            screen; elsewhere it is a global lookup, and Enter takes you to the results. */}
        <div className="hidden px-4 pb-2.5 lg:block">
          <form
            className="relative"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(q);
            }}
          >
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
              <IconSearch size={17} />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={onCaseList ? 'Filter these cases…' : SEARCH_PLACEHOLDER}
              aria-label="Search cases"
              type="search"
              className="h-10 w-full rounded-lg border border-line bg-surface-2/50 pl-11 pr-3 text-sm text-ink transition placeholder:text-muted focus:border-accent/50 focus:bg-surface focus:outline-none"
            />
          </form>
        </div>
      </div>

      {/* min-h-0 is what lets a child (the board) fill the remaining height instead of
          overflowing it - without it a flex child refuses to shrink below its content. */}
      <div className="min-h-0 flex-1 p-3 lg:p-4">
        <Outlet />
      </div>

      <Sheet open={searchOpen} onClose={() => setSearchOpen(false)} title="Search cases" initialFocus={searchField}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(q);
          }}
          className="space-y-3 pt-1"
        >
          <input
            ref={searchField}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={SEARCH_PLACEHOLDER}
            aria-label="Search cases"
            type="search"
            enterKeyHint="search"
            className={inputCls}
          />
          <p className="text-xs text-muted">
            Matches patient name, group ID, case number, phone and courier tracking. Completed and
            cancelled cases are included.
          </p>
          <Btn tone="accent" type="submit" className="w-full py-2.5">
            Search
          </Btn>
        </form>
      </Sheet>

      <NewWfCaseModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(c) => {
          setShowNew(false);
          navigate(`/workflow/cases/${c.id}`);
        }}
      />
    </div>
  );
}

function Badge({ n, className = '' }: { n: number; className?: string }) {
  return (
    <span
      className={`tabnum absolute min-w-[1.05rem] rounded-full bg-status-urgent-fg px-1 text-center text-[10px] font-bold leading-4 text-white ${className}`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
